import React from "react";
//#region types
import { CostForwardingSource } from "models/utilTypes/costForwardingTypes";
import {
  getCostForwardingReadId,
  getShownCostForwardingMarking,
  isCostForwardingLocked,
  withoutCarriedCostForwarding,
} from "./costForwardingMarkingModel";
//#endregion
//#region components
import useInvoicePermissions from "components/invoice/useInvoicePemissions";
import useCostForwardingMarking from "./useCostForwardingMarking";
import useCostForwardingMenuAction from "./useCostForwardingMenuAction";
import { CostForwardingMarkingContextValue } from "./costForwardingMarkingContext";
import { getCostForwardingSavedEventName } from "./useSaveCostForwardingMarking";
//#endregion
import { EventHelper } from "utils/helpers";

type FooterSubmit = (values: any) => Promise<any>;

/**
 * The marking on a receipt or supplier invoice page: its details section, its more-actions entry and its footer.
 * `isForwardable` is false once the document's cost can never reach a customer invoice.
 */
function useDocumentPageCostForwarding(
  source: CostForwardingSource,
  sourceId: number | undefined,
  supplierName: string | undefined,
  isForwardable: boolean,
  reloadToken: unknown
) {
  const { canUseInvoices } = useInvoicePermissions();
  const costForwarding = useCostForwardingMarking(
    source,
    getCostForwardingReadId(sourceId, canUseInvoices),
    reloadToken
  );
  const { reload } = costForwarding;

  // A ref rather than a patch of the details form's values, because a reinitialisation of that form
  // would restore the carried marking from its initial values.
  const hasPageWrittenMarking = React.useRef(false);
  const onMarkingWritten = React.useCallback(() => {
    hasPageWrittenMarking.current = true;
    reload();
  }, [reload]);

  // Saving the carried marking from the footer leaves the document's state, the reload token, unchanged.
  React.useEffect(() => {
    if (!sourceId) return;

    const eventName = getCostForwardingSavedEventName(source, sourceId);
    EventHelper.subscribeEvent(eventName, reload);
    return () => EventHelper.unsubscribeEvent(eventName, reload);
  }, [source, sourceId, reload]);

  const menuAction = useCostForwardingMenuAction(
    source,
    sourceId,
    supplierName,
    { ...costForwarding, isForwardable },
    onMarkingWritten
  );

  /** Wraps a footer's save so it never writes a carried marking over one this page has written. */
  const withoutStaleMarking = (submit?: FooterSubmit): FooterSubmit | undefined =>
    submit && ((values) => submit(withoutCarriedCostForwarding(values, hasPageWrittenMarking.current)));

  const shownMarking = getShownCostForwardingMarking(costForwarding.marking, isForwardable);
  const context: CostForwardingMarkingContextValue = {
    marking: shownMarking,
    isAvailable: costForwarding.isAvailable,
    reload: onMarkingWritten,
    editAction: shownMarking && !isCostForwardingLocked(shownMarking.state) ? menuAction : undefined,
  };

  return { context, menuAction, withoutStaleMarking };
}

export default useDocumentPageCostForwarding;
